import { useSelector } from 'react-redux'
import { useState, useContext } from 'react'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { ModalContext } from '../ModalContext'
import moment from 'moment'

import OutlineButton from 'components/Buttons/OutlineButton'

const cancellationEffects = [
    'Athlete data will no longer be visible to any coaches in your organization',
    'Athlete will no longer be able to complete workouts',
    'Pausing an athlete will schedule them to be automatically added back at the end of the pause'
]

const cancellationSurveyOptions = [
    {
        id: 1,
        key: 'direction',
        text: 'The product is hard to use',
    },
    {
        id: 2,
        key: 'direction',
        text: 'The athlete does not like the exercises',
    },
    {
        id: 3,
        key: 'direction',
        text: 'Inaccurate weight recommendations',
    },
    {
        id: 4,
        key: 'direction',
        text: 'The athlete raced/completed their program',
    },
    {
        id: 5,
        key: 'accountability',
        text: 'The athlete does not have time to strength train',
    },
    {
        id: 6,
        key: 'encouragement',
        text: 'The athlete is not motivated to keep training',
    },
    {
        id: 7,
        key: 'community',
        text: 'It is difficult for the athlete to share their progress',
    },
    {
        id: 8,
        key: 'results',
        text: 'The training is ineffective',
    },
    {
        id: 9,
        key: 'results',
        text: 'The athlete got injured',
    },
    {
        id: 10,
        key: 'budget',
        text: 'Too expensive',
    },
    {
        id: 11,
        key: 'other',
        text: 'Other',
    },
]

export default function AthleteOffboardModal() {

    const coachId = useSelector((state) => state.user.id)
    const organizationId = useSelector((state) => state.user.organizationId)
    const billingLocalTimestamp = useSelector((state) => state.organization.billingLocalTimestamp)
    
    const { modalData, HideModal } = useContext(ModalContext)

    const [modalRoute, setModalRoute] = useState('options')
    const [actionType, setActionType] = useState('')

    // Feedback variables

    const [selectedSurveyOptions, setSelectedSurveyOptions] = useState([])
    const [additionalNote, setAdditionalNote] = useState('')
    const [feedbackErrorText, setFeedbackErrorText] = useState('')
    const [feedbackSaving, setFeedbackSaving] = useState(false)

    // Remove athlete variables
    
    const [isRemovingAthlete, setIsRemovingAthlete] = useState(false)
    const [removeErrorMessage, setRemoveErrorMessage] = useState('')

    // Pause athlete variables
    
    const [pauseEndDate, setPauseEndDate] = useState(billingLocalTimestamp.clone().add(14, 'days').format('YYYY-MM-DD'))
    const [isPausingAthlete, setIsPausingAthlete] = useState(false)
    const [pauseErrorMessage, setPauseErrorMessage] = useState('')

    // Feedback functions

    const HandleSurveyOptionChange = (option) => {
        setFeedbackErrorText('')

        var temp_selectedOptions = JSON.parse(JSON.stringify(selectedSurveyOptions))

        // If the option is already present, remove it
        if (temp_selectedOptions.some((item) => item === option.id)) temp_selectedOptions = temp_selectedOptions.filter((item) => item !== option.id)
        // If the option is not present, add it
        else temp_selectedOptions.push(option.id)

        // console.log(temp_selectedOptions)

        setSelectedSurveyOptions(temp_selectedOptions)
    }

    const HandleAdditionalNotesChange = (event) => {
        setFeedbackErrorText('')
        if (event.target.value.length < 150) setAdditionalNote(event.target.value)
    }

    const SaveFeedback = async () => {
        if (selectedSurveyOptions.length === 0) {
            setFeedbackErrorText('Please select at least one removal reason')
            return
        }

        setFeedbackErrorText('')
        setFeedbackSaving(true)

        const jsonBody = {
            userId: modalData.athlete.id,
            coachId: coachId,
            source: 'coach-dashboard-billing',
            detailsJSON: {
                statusUponCancellation: modalData.athlete.status,
                selectedSurveyOptions: selectedSurveyOptions
            },
            additionalNote: additionalNote,
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('CORE', 'POST', '/user_feedback_item', jsonBody)

        // console.log(result)

        setFeedbackSaving(false)

        if (result.data.status === 'failure') {
            setFeedbackErrorText('Error saving feedback. Please try again.')
            console.error(result)
            return
        }

        if (actionType === 'pause') {
            setModalRoute('pause-date-selection')
            return
        } 

        if (actionType === 'remove') {
            await RemoveAthlete()
            HideModal()
        }
    }

    // Remove athlete functions

    const RemoveAthlete = async () => {

        setRemoveErrorMessage('')
        setIsRemovingAthlete(true)

        const jsonBody = {
            userId: coachId,
            organizationId: organizationId,
            athleteId: modalData.athlete.id,
            athleteStatus: modalData.athlete.status,
            membershipAction: 'offboard',
            localDateString: billingLocalTimestamp.format('YYYY-MM-DD'),
            utcOffsetMinutes: moment().utcOffset() // UTC offset in minutes for timezone-aware date calculations
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('BILLING', 'POST', '/update_organization_membership', jsonBody)

        // console.log(result)

        setIsRemovingAthlete(false)

        if (result.data.status === 'failure') {
            setRemoveErrorMessage('Error removing athlete. Please try again.')
            console.error(result)
            return
        }

        HideModal()

        window.location.reload()
    }

    // Pause athlete functions

    const PauseAthlete = async () => {

        setPauseErrorMessage('')
        setIsPausingAthlete(true)

        // Calculate the length of the pause in days. Add 1 to include the current day.
        // We need to send the pause length to the backend so we can get a timestamp that perserves the time data
        const pauseLengthDays = moment(pauseEndDate).diff(billingLocalTimestamp, 'days') + 1

        const jsonBody = {
            userId: coachId,
            organizationId: organizationId,
            athleteId: modalData.athlete.id,
            athleteStatus: modalData.athlete.status,
            membershipAction: 'pause',
            localDateString: billingLocalTimestamp.format('YYYY-MM-DD'),
            utcOffsetMinutes: moment().utcOffset(), // UTC offset in minutes for timezone-aware date calculations
            pauseLengthDays: pauseLengthDays
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('BILLING', 'POST', '/update_organization_membership', jsonBody)

        // console.log(result)

        setIsPausingAthlete(false)

        if (result.data.status === 'failure') {
            setPauseErrorMessage(result.data.message)
            console.error(result)
            return
        }

        HideModal()

        window.location.reload()
    }

    if (modalRoute === 'options') {
        return (
            <>
                <div className="text-center">
                    Are you sure you want to remove <strong>{modalData.athlete.firstName} {modalData.athlete.lastName}</strong>?
                </div>

                <p className='text-xs text-gray-600'>
                    Removal has the following effects:
                </p>

                <div className='space-y-2'>
                    {cancellationEffects.map((effect, index) => (
                        <div 
                            key={index} 
                            className='p-4 rounded-lg bg-gray-100'
                        >
                            <p className='text-left text-sm'>{effect}</p>
                        </div>
                    ))}
                </div>

                {(
                    modalData.athlete.status === 'free-trial'
                    || modalData.athlete.status === 'prorated-onboard'
                    || modalData.athlete.status === 'active'
                ) &&
                    <OutlineButton
                        text='Pause Athlete'
                        borderColor='border-blue-600'
                        textColor='text-blue-600'
                        action={() => {
                            setActionType('pause')
                            setModalRoute('feedback')
                        }}
                    />
                }

                <OutlineButton
                    text='Remove Athlete'
                    borderColor='border-red-500'
                    textColor='text-red-500'
                    action={() => {
                        setActionType('remove')
                        setModalRoute('feedback')
                    }}
                    isLoading={isRemovingAthlete}
                />

                <OutlineButton
                    text='Cancel'
                    borderColor='border-gray-400'
                    action={() => HideModal()}
                />

                {removeErrorMessage !== '' && 
                    <div>
                        <div className="-mt-2 text-xs text-red-500 font-bold">{removeErrorMessage}</div>
                    </div>
                }
            </>
        )
    }

    if (modalRoute === 'feedback') {
        return (
            <>
                <div className="text-center">
                    Why is <strong>{modalData.athlete.firstName} {modalData.athlete.lastName}</strong> being 
                    {' '}
                    {actionType === 'pause' ? 'paused' : 'removed'}?
                </div>

                <p className='text-xs text-gray-600'>Select all that apply</p>

                <div className="max-h-[50vh] overflow-y-auto divide-y divide-gray-300 rounded-xl">
                    {cancellationSurveyOptions.map((option) => (
                        <div 
                            key={option.id} 
                            className="py-3 px-4 flex items-center bg-gray-100 text-sm cursor-pointer hover:bg-gray-50" 
                            onClick={() => HandleSurveyOptionChange(option)}
                        >
                            <div className="text-left flex-grow text-sm">{option.text}</div>

                            {selectedSurveyOptions.length === 0 &&
                                <div className="h-4 w-4 border border-gray-400 rounded-full" />
                            }

                            {selectedSurveyOptions.length > 0 && !selectedSurveyOptions.some((item) => item === option.id) && 
                                <div className="h-4 w-4 border border-gray-400 rounded-full" />
                            }

                            {selectedSurveyOptions.length > 0 && selectedSurveyOptions.some((item) => item === option.id) && 
                                <div className='h-4 w-4 bg-gray-400 rounded-full p-0.5'>
                                    <div className=" h-3 w-3 border-2 border-white bg-gray-400 rounded-full" />
                                </div>
                            }
                        </div>
                    ))}
                </div>

                <div>
                    <textarea
                        type="text"
                        min="0"
                        name="additionalNotes"
                        id="additionalNotes"
                        className="h-20 w-full py-2 px-4 text-sm rounded-xl bg-gray-100 placeholder:text-gray-400"
                        placeholder={'Please provide us with any additional details.. '}
                        value={additionalNote}
                        onChange={(event) => HandleAdditionalNotesChange(event)}
                    />
                </div>

                {feedbackErrorText !== '' && 
                    <div>
                        <div className="-mt-2 text-xs text-red-500 font-bold">{feedbackErrorText}</div>
                    </div>
                }

                <OutlineButton
                    text={actionType === 'pause' ? 'Continue' : 'Remove Athlete'}
                    borderColor={actionType === 'pause' ? 'border-blue-600' : 'border-red-500'}
                    textColor={actionType === 'pause' ? 'text-blue-600' : 'text-red-500'}
                    action={() => SaveFeedback()}
                    isLoading={feedbackSaving || isRemovingAthlete}
                />
            </>
        )
    }

    if (modalRoute === 'pause-date-selection') {
        return (
            <>
                <div className="text-center">
                    Setup Pause for <strong>{modalData.athlete.firstName} {modalData.athlete.lastName}</strong>
                </div>

                <p className='text-xs text-gray-600'>
                    When would you like the pause to end? 90 max pause period.
                </p>

                <input
                    type="date"
                    className='w-full rounded-lg border border-gray-400 px-2 py-1 focus:outline-none cursor-pointer hover:bg-gray-50'
                    value={pauseEndDate}
                    min={billingLocalTimestamp.clone().add(1, 'days').format('YYYY-MM-DD')}
                    max={billingLocalTimestamp.clone().add(90, 'days').format('YYYY-MM-DD')}
                    onChange={(e) => setPauseEndDate(e.target.value)}
                />

                <OutlineButton
                    text='Pause Athlete'
                    borderColor='border-blue-600'
                    textColor='text-blue-600'
                    action={() => PauseAthlete()}
                    isLoading={isPausingAthlete}
                />

                <OutlineButton
                    text='Cancel'
                    action={() => HideModal()}
                />

                {pauseErrorMessage !== '' && 
                    <div>
                        <div className="-mt-2 text-xs text-red-500 font-bold">{pauseErrorMessage}</div>
                    </div>
                }
            </>
        )
    }
}
